import {
  DefaultFetchError,
  FetchCreate,
  FetchDelete,
  FetchGet,
  FetchGetId,
  FetchSuccess,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchDelete,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate,
} from "../fetch";
import dayjs, { Dayjs } from "dayjs";
import { User, UserStatusType } from "../../store/auth";

export interface UsersGetParams extends PagingParams {
  fullName?: string;
  orderByColumn?: "username" | "firstName";
}

export interface UserCreateParams extends Omit<
  User,
  | "birthdate"
  | "settings"
  | "isTwoFAActive"
  | "firstName"
  | "lastName"
  | "status"
> {
  birthdate?: Dayjs | null;
}

export type UserUpsetParams = Omit<UserCreateParams, "id" | "status">;

interface AdministratorsGetParams extends PagingParams {
  orderByColumn?: "username" | "firstName";
}

export const useUsersGetAll = <DD = UsersTableData>(
  decorateData?: (data: PagingDataResponse<User>) => DD,
): FetchGet<
  PagingDataResponse<User>,
  AdministratorsGetParams,
  DefaultFetchError,
  DD
> =>
  useFetchGet("users", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
  });

export interface UserTableRow extends Omit<User, "settings"> {
  key: string;
  // fullName: string;
}

export interface UsersTableData {
  data: UserTableRow[];
  total: number;
}

export const useUsersTableGet = () =>
  useUsersGetAll<UsersTableData>(
    ({ data, meta }: PagingDataResponse<User>): UsersTableData => ({
      data: data.map(
        ({ settings, ...item }: User): UserTableRow => ({
          key: item.id,
          // fullName: `${item.firstName || "-"}`,
          ...item,
        }),
      ),
      total: meta.itemCount,
    }),
  );

export const useUserGetById = <DD = User>(
  id?: string,
  decorateData?: (data: User) => DD,
): FetchGetId<User, DefaultFetchError, unknown, DD> =>
  useFetchGetId("users", id, {
    autoStart: !!id,
    decorateData,
  });

export const useUserCreate = (): FetchCreate<
  UserCreateParams,
  DefaultFetchError,
  UserUpsetParams
> => useFetchCreate("users");

export type UserUpdateParams = Partial<UserCreateParams>;

export const useUserUpdate = (
  id?: string,
): FetchUpdate<User, DefaultFetchError, UserUpdateParams> =>
  useFetchUpdate("users", id);

export const useUserDelete = (
  id?: string,
): FetchDelete<FetchSuccess, DefaultFetchError> => useFetchDelete("users", id);

export const useUsersGet = (): FetchGet<
  PagingDataResponse<User>,
  UsersGetParams
> => useFetchGet("users", { autoStart: false, startStateLoading: false });

export default {
  useUsersGet,
};
